import React from 'react';
import { cycleHex, markOn } from '../utils/cycleColors';

/**
 * The two quick-run cycle buttons.
 *
 * <h2>Naming</h2>
 * Everything a user can see — the label, the tooltip, the hover text, the drawn band — says only
 * the code and "Long Cycle" or "Short Cycle". That is a deliberate constraint on this whole feature
 * and not a stylistic preference, so the vocabulary is kept out of this module entirely rather than
 * merely kept off the screen: the quoted values below are wire identifiers the study endpoint
 * requires, and they are the only place any of it is allowed to appear. Nothing else in the file
 * interprets them and nothing renders them, which the sibling test enforces over this whole source
 * file rather than only over what reaches the screen.
 *
 * <p>The codes were LCI and SCI. They lived only here and in that test — there was no backend enum,
 * endpoint or payload carrying either, because the wire has always spoken in the study's own
 * vocabulary rather than in the button's. Renaming them was therefore a two-file change, not the
 * cross-stack refactor it looks like from the outside.
 *
 * <h2>What they draw</h2>
 * Flat shaded intervals and nothing else: no labels, no boundary markers, no badges, no journal
 * entry and no message panel. The buttons exist to put the intervals on the chart in one click and
 * then get out of the way, which is a different job from the configurable study that produces the
 * same intervals with its full reporting attached.
 */

/**
 * The two runs, in toolbar order.
 *
 * <p>`colorKey` is what the configured default colour is looked up by, so a band drawn from this
 * button and one drawn from the full study are the same colour — the study's own default rather
 * than a second palette invented here.
 */
export const CYCLE_BUTTONS = [
  // The three wire values on each entry are subject, sector and frame. `sector` is an index into
  // the endpoint's own twelve-part division, counting from zero, and `sectorName` is the same thing
  // spelled out - carried together so the test can assert they agree, because an index that
  // disagrees with its name runs the study on the wrong sector and still returns a plausible set of
  // dates. `frame` has the same property and is worse: a neighbouring value is valid and returns
  // different dates rather than an error, so it is written here once and nowhere else.
  {
    key: 'long',
    code: 'PTA',
    label: 'Long Cycle',
    subject: 'Venus',
    sector: 8,
    sectorName: 'Sagittarius',
    frame: 'helio',
    colorKey: 'Venus',
  },
  {
    key: 'short',
    code: 'PTB',
    label: 'Short Cycle',
    subject: 'Mercury',
    sector: 9,
    sectorName: 'Capricorn',
    frame: 'helio',
    colorKey: 'Mercury',
  },
];

/**
 * The ICONS entries for them.
 *
 * <h2>A mark on a filled plate</h2>
 * A stem with a disc at the TOP for the first and at the BOTTOM for the second — the two are told
 * apart by where the disc sits, which is the one difference that survives being small.
 *
 * <p>The plate is the part that matters for finding them. Two earlier attempts drew the mark
 * directly onto the toolbar, where a thin stroke on a gradient background competes with forty other
 * strokes; a solid block of colour does not compete with anything. The letters that replaced them
 * were found immediately, and the plate is what carried that — so the plate stays and only the
 * letters give way to the mark.
 */
export function cycleIconEntries() {
  const plate = (code, color, discAtTop) => {
    // The mark is COMPUTED from the plate, never typed beside it. See `markOn` for the measurement:
    // white reads 2.15:1 on PTA's amber and 2.45:1 on PTB's cyan, so repainting the plates to the
    // band hues without this would have left an unreadable glyph on both.
    const mark = markOn(color);
    return (
      <svg width="46" height="34" viewBox="0 0 46 34" aria-label={code} role="img">
        <rect x="1" y="1" width="44" height="32" rx="6" fill={color} />
        <line x1="23" y1={discAtTop ? 12 : 5} x2="23" y2={discAtTop ? 29 : 22}
          stroke={mark} strokeWidth="3" strokeLinecap="round" />
        <circle cx="23" cy={discAtTop ? 10 : 24} r="5.5" fill={mark} />
      </svg>
    );
  };

  return CYCLE_BUTTONS.map(({ code, label, key }) => ({
    // THE PLATE IS THE BAND'S OWN COLOUR, from `cycleHex` — the one place PTA and PTB are
    // declared, and the same call `cycleColors` makes for the bands themselves. It was `#1565c0`
    // and `#c62828`, a blue and a red that matched nothing on the chart: a reader had to learn
    // that the blue button draws amber bands and the red one draws cyan. Worse, `#c62828` is
    // LOSS_EXIT_COLOR, so the PTB button was the exact red that means a losing trade.
    component: plate(code, cycleHex(key), key === 'long'),
    alt: code,
    // The code and its expansion, on hover. The button carries no text now, so this is the only
    // place either appears.
    tooltip: `${code} — ${label}`,
    action: `run_cycle_${key}`,
    size: 48,
    // No `adminOnly` flag here, and that is not the same as "visible to everyone": both actions
    // are in ADMIN_ONLY_TOOLBAR_ACTIONS (config/nonAdminChrome.js), which is where the whole
    // PTA-to-Simulate block is named. The gate lives there rather than on these generated entries
    // so the six are one list a reader can check against a screenshot, instead of a flag on two
    // entries in this file and four in TabPanel.
  }));
}

/** The run a `run_cycle_*` action asks for, or null when the action is not one of these two. */
export function cycleForAction(action) {
  const match = /^run_cycle_(long|short)$/.exec(String(action || ''));
  return match ? CYCLE_BUTTONS.find(c => c.key === match[1]) || null : null;
}

/* ===============================================================================================
 * ONE CYCLE IS ONE COMPOUND OBJECT — the vertical pillars AND the horizontal bands
 * ===============================================================================================
 *
 * A cycle study draws two kinds of overlay that are meaningless apart. The pillars are the
 * intervals the study returns; the bands are the price range those intervals established, carried
 * forward. Either one alone is not half a study, it is a different and wrong one: pillars with no
 * bands say nothing about price, and bands with no pillars assert a range with no visible reason
 * for it.
 *
 * <h3>Why a STABLE group id rather than the context's automatic batching</h3>
 *
 * `ChartOverlayContext` stamps a groupId per macrotask, which is right for the ten-odd study
 * dialogs that add everything in one synchronous loop. A cycle does not:
 *
 *   click -> await the study -> add PILLARS -> setCycleRuns -> add BANDS        (one batch)
 *   ...React re-renders, the reveal effect fires -> REMOVE and RE-ADD the bands (a NEW batch)
 *
 * That second pass is not a bug — it is how a band appears as its interval closes during a
 * simulated reveal, and it re-renders EVERY drawn run, not just the one just clicked. But it
 * leaves the pillars in the click's batch and the bands in the effect's, so undo took one half
 * and left the other: PTA bands that "persist" after the pillars are gone, and that reappear the
 * next time any cycle is drawn.
 *
 * Deriving the group from the cycle KEY instead makes the two halves one unit no matter which
 * pass added them or how many ticks apart. Both halves of this rule are load-bearing: the id must
 * be stable across passes, and it must DIFFER between PTA and PTB or undoing one would take the
 * other with it.
 */

/** The single group id shared by every overlay of one cycle — pillars and bands alike. */
export function cycleGroupId(key) {
  return `cycle-${key}`;
}

/** The cycle key a group id belongs to, or null when the group is not a cycle's. */
export function cycleKeyForGroupId(groupId) {
  const match = /^cycle-(long|short)$/.exec(String(groupId || ''));
  return match ? match[1] : null;
}

/**
 * The id prefix for one cycle's overlays.
 *
 * <p>Note the BAND prefix starts with the pillar prefix, so clearing by the pillar prefix clears
 * both. That is wanted when a cycle is re-run and nowhere else — it is why the two are written
 * here together rather than inlined at two call sites that could drift apart.
 */
/**
 * WHAT THE HEADER PILL CALLS A CYCLE GROUP — `PTA — Long Cycle`, from its groupId.
 *
 * <h2>⚠ WHY THIS IS NOT THE OVERLAY'S `label`</h2>
 *
 * Because that field PRINTS ACROSS THE CHART. `handleRunCycleBand` sets `label: ''` deliberately —
 * "empty, not absent", because the renderer writes `overlay.label || ''` onto the band — so any
 * name put there appears over every shaded interval. The pill therefore has to derive the name
 * instead of reading it, which is what the groupId is already good for.
 *
 * <h2>⚠ AND IT IS WHY THE PILL SAID "Area highlight"</h2>
 *
 * The pill falls back to that string for a group with no label, and the cycle pillars have none.
 * So a PTA run put a chip reading `Area highlight` in the header — wrong on every product, not
 * only on integralDelta: a cycle band is not an Area Highlight study, it only shares the overlay
 * TYPE with one. The product that made it visible is not the product that made it wrong.
 *
 * <p>Same `${code} — ${label}` form the toolbar tooltip uses, composed from the same entry, so
 * the two cannot drift into two names for one control.
 *
 * @returns the display name, or null when the groupId is not a cycle's
 */
export function cycleGroupLabel(groupId) {
  const key = cycleKeyForGroupId(groupId);
  if (!key) return null;
  const button = CYCLE_BUTTONS.find((b) => b.key === key);
  return button ? `${button.code} — ${button.label}` : null;
}

export function cyclePillarIdPrefix(key) {
  return `cycle-band-${key}-`;
}

/** The id prefix for one cycle's horizontal price bands. */
export function cycleBandIdPrefix(key) {
  return `cycle-band-${key}-band-`;
}
